// pages/Medicines/AllMedicines.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import Breadcrumb from "components/Common/Breadcrumb";
import TableContainer from "components/Common/DataTableContainer";
import DashboardLoader from "components/Loader/DashboardLoader";
import DeleteModal from "pages/Calendar/DeleteModal";
import { medicineApiService } from "services/medicineApiService";
import MedicineDetailsModal from "./MedicineDetailsModal";
import UpdateMRPModal from "components/Products/UpdateMRPModal";

const AllMedicines = () => {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState("");
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  console.log("selectedMedicine selectedMedicine", selectedMedicine);
  const [refreshing, setRefreshing] = useState(false);
  const [globalDiscount, setGlobalDiscount] = useState(0);

  // Fetch all medicines
  const fetchMedicines = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    else setRefreshing(true);

    try {
      const [medicinesData, discountData] = await Promise.all([
        medicineApiService.getAllMedicines(),
        medicineApiService.getMedicineDiscount()
      ]);

      setMedicines(medicinesData);
      setGlobalDiscount(parseFloat(discountData.medicinediscount) || 0);
    } catch (error) {
      toast.error("Failed to fetch medicines");
      console.error("Error fetching medicines:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMedicines();
  }, [fetchMedicines]);

  // Handle medicine search
  const handleSearch = useCallback(async () => {
    setLoading(true);
    try {
      const results = await medicineApiService.searchMedicines(
        searchQuery,
        categoryFilter,
        brandFilter,
        availabilityFilter
      );
      setMedicines(results);
    } catch (error) {
      toast.error("Search failed");
      console.error("Error searching medicines:", error);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, categoryFilter, brandFilter, availabilityFilter]);

  // Handle medicine deletion
  const handleDeleteMedicine = async () => {
    if (!selectedMedicine) return;

    try {
      await medicineApiService.deleteMedicine(selectedMedicine.id);
      toast.success("Medicine deleted successfully");

      // Remove from local state
      setMedicines(prev => prev.filter(medicine => medicine.id !== selectedMedicine.id));
      setDeleteModalOpen(false);
    } catch (error) {
      toast.error("Failed to delete medicine");
      console.error("Error deleting medicine:", error);
    }
  };

  // Handle MRP update
  const handleUpdateMRP = async (newMrp) => {
    if (!selectedMedicine) return;

    const updatedItem = {
      id: selectedMedicine.id,
      regularunitprice: newMrp
    };
    try {
      await medicineApiService.updateMedicine(updatedItem);
      toast.success("Medicine MRP updated successfully");

      // Update local state
      setMedicines(prev =>
        prev.map(medicine =>
          medicine.id === selectedMedicine.id
            ? { ...medicine, mrp: newMrp }
            : medicine
        )
      );

      setUpdateModalOpen(false);
    } catch (error) {
      toast.error("Failed to update MRP");
      console.error("Error updating MRP:", error);
    }
  };

  // Show medicine details
  const handleShowDetails = (medicine) => {
    setSelectedMedicine(medicine);
    setDetailsModalOpen(true);
  };

  // Get unique categories for filter
  const uniqueCategories = useMemo(() => {
    const categories = medicines.map(medicine => medicine.category).filter(Boolean);
    return [...new Set(categories)];
  }, [medicines]);

  // Get unique brands for filter
  const uniqueBrands = useMemo(() => {
    const brands = medicines.map(medicine =>
      medicine.productbrand || medicine.companyname
    ).filter(Boolean);
    return [...new Set(brands)];
  }, [medicines]);

  // Filter medicines locally for instant search
  const filteredMedicines = useMemo(() => {
    if (!medicines.length) return [];

    return medicines.filter(medicine => {
      // Search filter
      const matchesSearch = !searchQuery ||
        (medicine.productname || medicine.medicinename || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (medicine.productbrand || medicine.companyname || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (medicine.genericname || '').toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      const matchesCategory = !categoryFilter || medicine.category === categoryFilter;

      // Brand filter
      const matchesBrand = !brandFilter ||
        medicine.productbrand === brandFilter ||
        medicine.companyname === brandFilter;

      // Availability filter
      const matchesAvailability = !availabilityFilter ||
        (availabilityFilter === 'available' && medicine.availablestatus) ||
        (availabilityFilter === 'unavailable' && !medicine.availablestatus);

      return matchesSearch && matchesCategory && matchesBrand && matchesAvailability;
    });
  }, [medicines, searchQuery, categoryFilter, brandFilter, availabilityFilter]);

  // Format currency
  const formatCurrency = (amount) => {
    return `৳${parseFloat(amount || 0).toLocaleString('en-BD', { minimumFractionDigits: 2 })}`;
  };

  // Calculate discounted price
  const calculateDiscountedPrice = (mrp) => {
    const price = parseFloat(mrp || 0);
    const discountPrice = price - (price * (globalDiscount / 100));
    return discountPrice.toFixed(2);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    return status ? (
      <span className="badge bg-success">
        <i className="fas fa-check me-1"></i> Available
      </span>
    ) : (
      <span className="badge bg-danger">
        <i className="fas fa-times me-1"></i> Unavailable
      </span>
    );
  };

  // Columns for the table
  const columns = useMemo(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => `#${row.original.id}`,
      },
      {
        header: "Image",
        accessorKey: "imagelink",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex justify-content-center">
            <img
              src={row.original.imagelink || "https://static.vecteezy.com/system/resources/thumbnails/002/272/156/small_2x/medicine-drug-icon-flat-style-and-colorful-design-illustration-free-vector.jpg"}
              alt={row.original.productname || row.original.medicinename}
              className="rounded"
              style={{ width: "50px", height: "50px", objectFit: "cover" }}
              onError={(e) => {
                e.target.src = "https://static.vecteezy.com/system/resources/thumbnails/002/272/156/small_2x/medicine-drug-icon-flat-style-and-colorful-design-illustration-free-vector.jpg";
                e.target.onerror = null;
              }}
            />
          </div>
        ),
      },
      {
        header: "Medicine Name",
        accessorKey: "productname",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div className="fw-semibold">
              {row.original.productname || row.original.medicinename}
            </div>
            <small className="text-muted d-block">
              {row.original.genericname || "N/A"}
            </small>
            <small className="text-muted">
              {row.original.power || row.original.size || "N/A"}
            </small>
          </div>
        ),
      },
      {
        header: "Brand/Company",
        accessorKey: "productbrand",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div>
            <div>{row.original.productbrand || row.original.companyname || "N/A"}</div>
            <small className="text-muted">{row.original.vandor || "N/A"}</small>
          </div>
        ),
      },
      // {
      //   header: "Category",
      //   accessorKey: "category",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => (
      //     <div>
      //       <span className="badge bg-info me-1">{row.original.category || "N/A"}</span>
      //       {row.original.subcategory && (
      //         <span className="badge bg-secondary">{row.original.subcategory}</span>
      //       )}
      //     </div>
      //   ),
      // },
      {
        header: "Price",
        accessorKey: "mrp",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => {
          const mrp = parseFloat(row.original.mrp || row.original.regularunitprice || 0);
          const discountPrice = calculateDiscountedPrice(mrp);

          return (
            <div>
              <div className="fw-bold text-primary">
                {formatCurrency(discountPrice)}
              </div>
              <div className="text-muted small text-decoration-line-through">
                {formatCurrency(mrp)}
              </div>
              <small className="text-success">
                {globalDiscount}% off
              </small>
            </div>
          );
        },
      },
      {
        header: "Packaging",
        accessorKey: "packaging",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <div className="">
            <span className="badge bg-warning">{row.original.packaging || row.original.types || "N/A"}</span>
            <div className="mt-1">
              <small className="text-muted">Qty: {row.original.quantity || "1"}</small>
            </div>
          </div>
        ),
      },

      // {
      //   header: "Status",
      //   accessorKey: "availablestatus",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      //   cell: ({ row }) => getStatusBadge(row.original.availablestatus),
      // },
      {
        header: "Actions",
        accessorKey: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-1">
              <button
                onClick={() => handleShowDetails(row.original)}
                className="btn btn-info btn-sm"
                title="View Details"
              >
                <i className="fas fa-eye"></i>
              </button>
              <button
                onClick={() => {
                  setSelectedMedicine(row.original);
                  setUpdateModalOpen(true);
                }}
                className="btn btn-warning btn-sm"
                title="Update MRP"
              >
                <i className="fas fa-edit"></i>
              </button>
              {/* <button
                onClick={() => {
                  setSelectedMedicine(row.original);
                  setDeleteModalOpen(true);
                }}
                className="btn btn-danger btn-sm"
                title="Delete Medicine"
              >
                <i className="fas fa-trash"></i>
              </button> */}
            </div>
          </div>
        ),
      },
    ],
    [globalDiscount]
  );

  // Statistics
  const totalMedicines = medicines.length;
  const availableMedicines = medicines.filter(m => m.availablestatus).length;
  const totalMRPValue = medicines.reduce((sum, medicine) =>
    sum + parseFloat(medicine.mrp || medicine.regularunitprice || 0), 0
  );
  const totalDiscountedValue = medicines.reduce((sum, medicine) => {
    const mrp = parseFloat(medicine.mrp || medicine.regularunitprice || 0);
    return sum + (mrp - (mrp * (globalDiscount / 100)));
  }, 0);

  if (loading) {
    return <DashboardLoader />;
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Medicines" breadcrumbItem="All Medicines" />

        {/* Statistics Cards */}
        {/* <div className="row mb-4">
          <div className="col-md-3">
            <div className="card border-primary border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Total Medicines</p>
                    <h4 className="mt-2">{totalMedicines}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-primary rounded-circle">
                      <i className="fas fa-pills font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-success border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Available</p>
                    <h4 className="mt-2">{availableMedicines}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-success rounded-circle">
                      <i className="fas fa-check-circle font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-warning border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">Total MRP Value</p>
                    <h4 className="mt-2">{formatCurrency(totalMRPValue)}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-warning rounded-circle">
                      <i className="fas fa-money-bill-wave font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card border-info border-opacity-25">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p className="text-muted mb-0">After Discount ({globalDiscount}%)</p>
                    <h4 className="mt-2">{formatCurrency(totalDiscountedValue)}</h4>
                  </div>
                  <div className="avatar-sm">
                    <span className="avatar-title bg-info rounded-circle">
                      <i className="fas fa-percentage font-size-24"></i>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div> */}

        {/* Search and Filter Bar */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="fas fa-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search medicines by name, brand, generic name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                  />
                  {searchQuery && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => {
                        setSearchQuery("");
                        fetchMedicines(false);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* <div className="col-md-2">
                <select
                  className="form-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {uniqueCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div> */}

              {/* <div className="col-md-2">
                <select
                  className="form-select"
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                >
                  <option value="">All Brands</option>
                  {uniqueBrands.map(brand => (
                    <option key={brand} value={brand}>
                      {brand}
                    </option>
                  ))}
                </select>
              </div> */}

              {/* <div className="col-md-2">
                <select
                  className="form-select"
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="available">Available</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div> */}

              <div className="col-md-2">
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-success"
                    onClick={() => handleSearch()}
                    disabled={refreshing}
                  >
                    {refreshing ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      <i className="fas fa-search me-2"></i>
                    )}
                  </button>
                  <button
                    className="btn btn-light"
                    onClick={() => {
                      setSearchQuery("");
                      setCategoryFilter("");
                      setBrandFilter("");
                      setAvailabilityFilter("");
                      fetchMedicines(false);
                    }}
                    disabled={!searchQuery && !categoryFilter && !brandFilter && !availabilityFilter}
                  >
                    <i className="fas fa-times me-1"></i> Clear
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Medicines Table */}
        <div className="card">
          <div className="card-body">
            {filteredMedicines.length === 0 ? (
              <div className="text-center py-5">
                <i className="fas fa-pills fa-3x text-muted mb-3"></i>
                <h4>No medicines found</h4>
                <p className="text-muted">
                  {searchQuery || categoryFilter || brandFilter || availabilityFilter
                    ? 'Try adjusting your search filters'
                    : 'No medicines available in the system'}
                </p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => fetchMedicines(false)}
                >
                  <i className="fas fa-sync-alt me-2"></i> Refresh List
                </button>
              </div>
            ) : (
              <TableContainer
                columns={columns}
                data={filteredMedicines}
                isGlobalFilter={false}
                isPagination={true}
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                isCustomPageSize={true}
              />
            )}
          </div>
        </div>


      </div>

      {/* Modals */}
      {selectedMedicine && (
        <>
          <MedicineDetailsModal
            isOpen={detailsModalOpen}
            toggle={() => setDetailsModalOpen(false)}
            medicine={selectedMedicine}
          />

          <UpdateMRPModal
            isOpen={updateModalOpen}
            toggle={() => setUpdateModalOpen(false)}
            product={selectedMedicine}
            onUpdate={handleUpdateMRP}
          />
        </>
      )}

      <DeleteModal
        show={deleteModalOpen}
        onDeleteClick={handleDeleteMedicine}
        loader={false}
        onCloseClick={() => setDeleteModalOpen(false)}
        title="Delete Medicine"
        message="Are you sure you want to delete this medicine? This action cannot be undone."
      />
    </div>
  );
};

export default AllMedicines;